import React, {FC, ReactElement} from "react";
import {Alert, Button, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, TextField} from "@mui/material";
import LoadingButton from "../../../components/button/LoadingButton";
import {observer} from "mobx-react";
import {ContractType, newEmployeeWizardState, RateType} from "../../../store/NewEmployeeWizardState";
import {ControlDivider} from "./NewEmployeeWizard";
import NumberFormat from "react-number-format";
import YesNoRadioGroup from "../../../components/input/YesNoRadioGroup";
import {DatePicker} from "../../../components/picker/DatePicker";
import moment from "moment";
import {contractTypeLabel, salaryAmountLabelWithBrackets} from "./utils";
import Tooltip from "../../../components/Tooltip";
import {Trans, withTranslation} from "react-i18next";
import {trans} from "../../../locale/utils";

interface IProps {
    handleBack: () => void
    handleNext: () => void
}

export const ContractDetailsSection: FC<IProps> = observer(({handleNext, handleBack}): ReactElement => {


    const nextDisabled =
            !newEmployeeWizardState.contractType ||
            !newEmployeeWizardState.position ||
            !newEmployeeWizardState.workAddress ||
            !newEmployeeWizardState.rateType ||
            !newEmployeeWizardState.rateValue ||
            (!newEmployeeWizardState.worksFullTime && !newEmployeeWizardState.partTimeAllocationValue) ||
            !newEmployeeWizardState.contractStartDate ||
            (newEmployeeWizardState.contractHasEndDate === undefined || (newEmployeeWizardState.contractHasEndDate && !newEmployeeWizardState.contractEndDate)) ||
            (newEmployeeWizardState.probationPeriod === undefined || (newEmployeeWizardState.probationPeriod && !newEmployeeWizardState.probationPeriodMonths)) ||
            false;
    return <>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <FormControl>
                    <FormLabel required><Trans i18nKey="new_employee.contract_details.contract_type">Contract type</Trans></FormLabel>
                    <RadioGroup row value={newEmployeeWizardState.contractType ?? ""}
                                onChange={e => {
                                    // @ts-ignore
                                    newEmployeeWizardState.contractType = e.target.value;
                                }}>
                        {
                            Object.values(ContractType)
                                    .map(it =>
                                            <FormControlLabel key={it} value={it} control={<Radio/>} label={contractTypeLabel(it)}/>
                                    )
                        }
                    </RadioGroup>
                </FormControl>
            </Grid>
            {newEmployeeWizardState.contractType !== undefined && !newEmployeeWizardState.performsWorkInEstonia && newEmployeeWizardState.contractType !== ContractType.BOARD_MEMBER &&
                    <Grid item xs={12}>
                        <Alert severity="warning">
                            <Trans i18nKey="new_employee.contract_details.impossible_to_register">
                                It's impossible to register employee who performs duties outside of Estonia in Employment register
                            </Trans>
                        </Alert>
                    </Grid>
            }
            <Grid item container xs={12}>
                <Grid item xs={12} md={5}>
                    <TextField
                            required
                            label={trans("new_employee.contract_details.position", "Position")}
                            fullWidth
                            placeholder={"Credit Manager"}
                            value={newEmployeeWizardState.position ?? ""}
                            onChange={e => newEmployeeWizardState.position = e.target.value}
                    />
                </Grid>
            </Grid>
            <Grid item container xs={12} alignItems={"flex-end"}>
                <Grid item xs={12} md={5}>
                    <TextField
                            required
                            label={trans("new_employee.contract_details.work_address", "Work address")}
                            fullWidth
                            placeholder={"Lossi plats 1a-11, 15165, Tallinn, Estonia"}
                            value={newEmployeeWizardState.workAddress ?? ""}
                            onChange={e => newEmployeeWizardState.workAddress = e.target.value}
                    />
                </Grid>
                <Grid item>
                    <Tooltip variant="subtitle1">
                        <Trans i18nKey="new_employee.contract_details.work_address_tip">
                            You should specify an exact location where the employee will perform duties.<br/>
                            In case of remote workers - please specify the worker's home address.<br/>
                            If the employee is a truck driver or sailor, please use the company's legal address.
                        </Trans>
                    </Tooltip>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <FormControl>
                    <FormLabel required><Trans i18nKey="new_employee.contract_details.rate_type">Rate type</Trans></FormLabel>
                    <RadioGroup row value={newEmployeeWizardState.rateType ?? ""}
                                onChange={e => {
                                    // @ts-ignore
                                    newEmployeeWizardState.rateType = e.target.value;
                                }}>
                        <FormControlLabel value={RateType.HOURLY} control={<Radio/>} label={trans("new_employee.contract_details.hourly_rate", "Hourly rate")}/>
                        <FormControlLabel value={RateType.MONTHLY} control={<Radio/>} label={trans("new_employee.contract_details.monthly_rate", "Monthly rate")}/>
                    </RadioGroup>
                </FormControl>
            </Grid>
            <Grid item container xs={12} alignItems={"flex-end"}>
                <Grid item xs={12} md={5}>
                    <NumberFormat
                            required
                            label={<>
                                <Trans i18nKey="new_employee.contract_details.salary">Gross/brutto salary rate</Trans>{" "}
                                {salaryAmountLabelWithBrackets(newEmployeeWizardState.rateType)}
                            </>}
                            fullWidth
                            customInput={TextField}
                            thousandSeparator={'\''}
                            value={newEmployeeWizardState.rateValue}
                            decimalScale={2}
                            onValueChange={(values) => {
                                newEmployeeWizardState.rateValue = values.value;
                            }}
                            isNumericString
                    />
                </Grid>
                <Grid item>
                    <Tooltip variant="subtitle1">
                        <Trans i18nKey="new_employee.contract_details.salary_tip">
                            Based on labour legislation you can not specify net salary.<br/>
                            <b>You should specify gross/brutto salary</b><br/>
                            You can use <a href="https://www.kalkulaator.ee/et/palgakalkulaator" target="_blank" rel="noopener noreferrer">salary calculator</a> to calculate
                            gross/brutto amount<br/>
                            <b>In 2022 minimum wage is 654 EUR per month and 3.86 EUR per hour</b>
                        </Trans>
                    </Tooltip>
                </Grid>
            </Grid>
            <Grid item container xs={12} spacing={2} alignItems="center">
                <Grid item>
                    <YesNoRadioGroup
                            required={true}
                            label={trans("new_employee.contract_details.works_full_time", "Does employee works full time?")}
                            value={newEmployeeWizardState.worksFullTime ?? null}
                            onChange={newValue => {
                                newEmployeeWizardState.worksFullTime = newValue
                                newEmployeeWizardState.partTimeAllocationValue = undefined
                            }}
                    />
                </Grid>
                {newEmployeeWizardState.worksFullTime === false &&
                        <Grid item>
                            <FormControl>
                                <FormLabel required><Trans i18nKey="new_employee.contract_details.time_allocation">Time allocation</Trans></FormLabel>
                                <RadioGroup row value={newEmployeeWizardState.partTimeAllocationValue ?? ""}
                                            onChange={e => {
                                                // @ts-ignore
                                                newEmployeeWizardState.partTimeAllocationValue = e.target.value;
                                            }}>
                                    <FormControlLabel value={"0.25"} control={<Radio/>} label="0.25"/>
                                    <FormControlLabel value={"0.5"} control={<Radio/>} label="0.5"/>
                                    <FormControlLabel value={"0.75"} control={<Radio/>} label="0.75"/>
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                }
                <Grid item>
                    <Tooltip variant="subtitle1">
                        <Trans i18nKey="new_employee.contract_details.works_full_time_tip">
                            If you want employee performing duties every working day - you should answer <b>Yes</b> here<br/>
                            In case of part-time allocation 1 means full-time work, 0.5 means employee works half of the month
                        </Trans>
                    </Tooltip>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <DatePicker
                        label={trans("new_employee.contract_details.start_date", "Contract start date *")}
                        value={newEmployeeWizardState.contractStartDate ?? null}
                        onChange={(date) => newEmployeeWizardState.contractStartDate = date!!}
                />
            </Grid>

            <Grid item container xs={12} spacing={2} alignItems={"flex-end"}>
                <Grid item>
                    <YesNoRadioGroup
                            required={true}
                            label={trans("new_employee.contract_details.has_end_date", "Contract has termination date?")}
                            value={newEmployeeWizardState.contractHasEndDate ?? null}
                            onChange={newValue => {
                                newEmployeeWizardState.contractHasEndDate = newValue
                                newEmployeeWizardState.contractEndDate = undefined
                            }}
                    />
                </Grid>
                {newEmployeeWizardState.contractHasEndDate === true &&
                        <Grid item>
                            <DatePicker
                                    label={trans("new_employee.contract_details.end_date", "Termination date *")}
                                    value={newEmployeeWizardState.contractEndDate ?? null}
                                    onChange={(date) => newEmployeeWizardState.contractEndDate = date!!}
                                    minDate={(newEmployeeWizardState.contractStartDate ?? moment()).clone().add(1, 'day')}
                            />
                        </Grid>
                }
            </Grid>

            <Grid item container xs={12} spacing={2} alignItems={"center"}>
                <Grid item>
                    <YesNoRadioGroup
                            required={true}
                            label={trans("new_employee.contract_details.probation_period", "Contract has probation period?")}
                            value={newEmployeeWizardState.probationPeriod ?? null}
                            onChange={newValue => {
                                newEmployeeWizardState.probationPeriod = newValue
                                newEmployeeWizardState.probationPeriodMonths = undefined
                            }}
                    />
                </Grid>
                {newEmployeeWizardState.probationPeriod === true &&
                        <> <Grid item>
                            <FormControl>
                                <FormLabel required><Trans i18nKey="new_employee.contract_details.probation_period_len">Probation period length (in months)</Trans></FormLabel>
                                <RadioGroup row value={newEmployeeWizardState.probationPeriodMonths ?? ""}
                                            onChange={e => {
                                                // @ts-ignore
                                                newEmployeeWizardState.probationPeriodMonths = e.target.value;
                                            }}>
                                    <FormControlLabel value={"1"} control={<Radio/>} label="1"/>
                                    <FormControlLabel value={"2"} control={<Radio/>} label="2"/>
                                    <FormControlLabel value={"3"} control={<Radio/>} label="3"/>
                                    <FormControlLabel value={"4"} control={<Radio/>} label="4"/>
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                            <Grid item>
                                <Tooltip variant="subtitle1">
                                    <Trans i18nKey="new_employee.contract_details.probation_period_len_tip">
                                        4 months is max allowed probation period by Estonian law
                                    </Trans>
                                </Tooltip>
                            </Grid>
                        </>
                }
            </Grid>
        </Grid>
        <ControlDivider/>
        <Grid container spacing={1}>
            <Grid item>
                <Button variant="contained" color="secondary" onClick={handleBack}><Trans i18nKey="common.back">Back</Trans></Button>
            </Grid>
            <Grid item>
                <LoadingButton disabled={nextDisabled} variant="contained" color="primary" onClick={handleNext}><Trans i18nKey="common.next">Next</Trans></LoadingButton>
            </Grid>
        </Grid>
    </>
});

export default withTranslation()(ContractDetailsSection);